/**
 * StandupAgentRow Component
 * AIDLC Administrative Backend
 *
 * Individual agent standup entry. Expandable row showing yesterday/today/blockers.
 */

import { useState } from 'react';
import type { StandupEntry } from '../../../types/projects';

interface StandupAgentRowProps {
  entry: StandupEntry;
  agentName: string;
}

function getInitials(name: string): string {
  return name
    .split(/[\s-]+/)
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
}

export function StandupAgentRow({ entry, agentName }: StandupAgentRowProps) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden dark:bg-zinc-900 dark:border-zinc-800">
      {/* Header -- clickable to expand/collapse */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-3 p-3 hover:bg-gray-50 transition-colors text-left"
      >
        {/* Agent avatar */}
        <div className="w-8 h-8 rounded-full bg-ldaja-blue/10 text-ldaja-blue flex items-center justify-center text-xs font-bold flex-shrink-0">
          {getInitials(agentName)}
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{agentName}</div>
          {entry.isAutoGenerated && (
            <span className="text-[9px] px-1.5 py-0.5 bg-cyan-50 text-cyan-700 rounded">auto-generated</span>
          )}
        </div>

        {/* Blocker indicator */}
        {entry.blockers && entry.blockers.trim().toLowerCase() !== 'none' && (
          <span className="text-[10px] px-2 py-0.5 bg-red-50 text-red-700 rounded-full font-medium flex-shrink-0">
            Has blockers
          </span>
        )}

        {/* Chevron */}
        <svg
          className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${expanded ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-3 pb-3 pl-14 space-y-2">
          <div>
            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Yesterday</div>
            <p className="text-xs text-gray-700 dark:text-gray-300">{entry.yesterday || 'No update.'}</p>
          </div>
          <div>
            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Today</div>
            <p className="text-xs text-gray-700 dark:text-gray-300">{entry.today || 'No update.'}</p>
          </div>
          <div>
            <div className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-0.5">Blockers</div>
            <p className={`text-xs ${
              entry.blockers && entry.blockers.trim().toLowerCase() !== 'none'
                ? 'text-red-600 font-medium'
                : 'text-gray-500'
            }`}>
              {entry.blockers || 'None'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
